/**
 * LE CARNET DE VACCINATION, la logique sans ecran (testee par
 * `carnet.test.mjs`). Decisions du fondateur le 26/10/2026 : une ligne par
 * PROTOCOLE de prevention (la maladie couverte, ou le vermifuge), pas par
 * produit ; certains sont obligatoires, d'autres conseilles ; vaccins et
 * antiparasitaires dans le meme carnet.
 *
 * Un soin entre dans une ligne par son produit : le champ `prevention` du
 * produit (Catalogue) dit ce qu'il couvre, `reminderDelay` son delai de
 * rappel. La date de rappel d'un soin est la sienne (`reminderDate`) si elle
 * est posee, sinon sa date plus le delai du produit.
 */

export type Groupe = 'Vaccins' | 'Antiparasitaires';

export interface ProtocoleDuPlan {
  protocole: string;
  groupe: Groupe;
  obligatoire: boolean;
}

/**
 * LE PLAN PAR ESPECE : les protocoles attendus, obligatoires ou conseilles.
 * Une PROPOSITION, a valider avec Margaux et deux autres veterinaires. Les
 * valeurs sont celles du champ `prevention` des produits et du champ `species`
 * des animaux.
 */
export const PLAN_PAR_ESPECE: Readonly<Record<string, readonly ProtocoleDuPlan[]>> = {
  Chien: [
    { protocole: 'CHPPi', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Leptospirose', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Rage', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Toux de chenil', groupe: 'Vaccins', obligatoire: false },
    { protocole: 'Vermifuge', groupe: 'Antiparasitaires', obligatoire: true },
    { protocole: 'Puces et tiques', groupe: 'Antiparasitaires', obligatoire: false },
  ],
  Chat: [
    { protocole: 'Typhus-Coryza', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Leucose', groupe: 'Vaccins', obligatoire: false },
    { protocole: 'Rage', groupe: 'Vaccins', obligatoire: false },
    { protocole: 'Vermifuge', groupe: 'Antiparasitaires', obligatoire: true },
    { protocole: 'Puces et tiques', groupe: 'Antiparasitaires', obligatoire: false },
  ],
  Lapin: [
    { protocole: 'Myxomatose-VHD', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Vermifuge', groupe: 'Antiparasitaires', obligatoire: false },
  ],
  Cheval: [
    { protocole: 'Grippe équine', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Tétanos', groupe: 'Vaccins', obligatoire: true },
    { protocole: 'Vermifuge', groupe: 'Antiparasitaires', obligatoire: true },
  ],
};

/** Les protocoles antiparasitaires : un protocole hors plan s'y range s'il en est. */
const ANTIPARASITAIRES = ['Vermifuge', 'Puces et tiques'];

/** Les delais de rappel du champ `reminderDelay`, en mois et jours. */
const DELAIS: Readonly<Record<string, { mois?: number; jours?: number }>> = {
  '3 semaines': { jours: 21 },
  '1 mois': { mois: 1 },
  '3 mois': { mois: 3 },
  '6 mois': { mois: 6 },
  '1 an': { mois: 12 },
  '3 ans': { mois: 36 },
};

/**
 * LA DATE DE RAPPEL d'un soin fait le `jour` (`YYYY-MM-DD`) avec un produit au
 * delai `delai` ; null sans delai connu. Les mois d'abord, en restant dans le
 * mois (le 31 janvier plus un mois donne le 28 fevrier), puis les jours.
 */
export function dateDeRappel(jour: string, delai: unknown): string | null {
  const d = typeof delai === 'string' ? DELAIS[delai] : undefined;
  if (d === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null;
  const annee = Number(jour.slice(0, 4));
  const mois = Number(jour.slice(5, 7)) - 1 + (d.mois ?? 0);
  const dernier = new Date(annee, mois + 1, 0).getDate();
  const date = new Date(annee, mois, Math.min(Number(jour.slice(8, 10)), dernier));
  date.setDate(date.getDate() + (d.jours ?? 0));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Ce que le carnet sait d'un produit. */
export interface ProduitDuCarnet {
  nom: string;
  couvre: readonly string[];
  especes: readonly string[];
  delai: unknown;
}

/** Ce que le carnet sait d'un soin de l'animal. */
export interface SoinDuCarnet {
  id: string;
  productId: string;
  careDate: string;
  reminderDate: string | null;
  lotNumber: string;
}

export interface Injection {
  soinId: string;
  date: string;
  produit: string;
  lot: string;
}

export type EtatDuCarnet = 'retard' | 'bientot' | 'ok' | 'jamais';

export interface LigneDuCarnet extends ProtocoleDuPlan {
  /** La plus recente d'abord. */
  injections: Injection[];
  prochaine: string | null;
  etat: EtatDuCarnet;
}

/**
 * LES LIGNES DU CARNET de l'animal : le plan de son espece, puis tout protocole
 * deja fait hors du plan (conseille). Une ligne garde toutes ses injections,
 * la plus recente d'abord ; son etat vient de la date de rappel de la derniere :
 * en retard, bientot (dans les 30 jours), a jour, ou jamais fait.
 */
export function lignesDuCarnet(
  espece: unknown,
  soins: readonly SoinDuCarnet[],
  produits: ReadonlyMap<string, ProduitDuCarnet>,
  aujourdhui: Date,
): LigneDuCarnet[] {
  const plan = typeof espece === 'string' ? (PLAN_PAR_ESPECE[espece] ?? []) : [];
  const parProtocole = new Map<string, Array<{ soin: SoinDuCarnet; produit: ProduitDuCarnet }>>();
  for (const soin of soins) {
    const produit = produits.get(soin.productId);
    if (produit === undefined || soin.careDate === '') continue;
    for (const protocole of produit.couvre) parProtocole.set(protocole, [...(parProtocole.get(protocole) ?? []), { soin, produit }]);
  }
  const horsPlan: ProtocoleDuPlan[] = [...parProtocole.keys()]
    .filter((p) => !plan.some((x) => x.protocole === p))
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((p) => ({ protocole: p, groupe: ANTIPARASITAIRES.includes(p) ? 'Antiparasitaires' : 'Vaccins', obligatoire: false }));
  const jour = jourPurDe(aujourdhui);
  const dans30 = jourPurDe(new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate() + 30));
  return [...plan, ...horsPlan].map((p) => {
    const faits = [...(parProtocole.get(p.protocole) ?? [])].sort((a, b) => b.soin.careDate.localeCompare(a.soin.careDate));
    const injections = faits.map(({ soin, produit }) => ({ soinId: soin.id, date: soin.careDate, produit: produit.nom, lot: soin.lotNumber }));
    const derniere = faits[0];
    const prochaine = derniere === undefined ? null : (derniere.soin.reminderDate ?? dateDeRappel(derniere.soin.careDate, derniere.produit.delai));
    const etat: EtatDuCarnet =
      derniere === undefined ? 'jamais' : prochaine === null ? 'ok' : prochaine < jour ? 'retard' : prochaine <= dans30 ? 'bientot' : 'ok';
    return { ...p, injections, prochaine, etat };
  });
}

/**
 * L'ALERTE DU « PROCHAIN RAPPEL » de l'en-tete : une date passee ne se lit pas
 * seule, elle porte une pastille « En retard » ; dans les trente jours,
 * « Bientôt » ; au-dela, ou sans rappel du carnet, rien.
 */
export function alerteDuRappel(etat: EtatDuCarnet): { label: string; tone: 'danger' | 'accent' } | null {
  if (etat === 'retard') return { label: 'En retard', tone: 'danger' };
  if (etat === 'bientot') return { label: 'Bientôt', tone: 'accent' };
  return null;
}

/** Ce qu'il faut proposer aujourd'hui : en retard, puis bientot, puis les obligatoires jamais faits. */
export function aProposer(lignes: readonly LigneDuCarnet[]): LigneDuCarnet[] {
  return [
    ...lignes.filter((l) => l.etat === 'retard'),
    ...lignes.filter((l) => l.etat === 'bientot'),
    ...lignes.filter((l) => l.etat === 'jamais' && l.obligatoire),
  ];
}

/** Les produits qui couvrent un protocole pour une espece (une espece vide les accepte tous), par nom. */
export function produitsPour(protocole: string, espece: unknown, produits: ReadonlyMap<string, ProduitDuCarnet>): Array<{ id: string; nom: string }> {
  return [...produits.entries()]
    .filter(([, p]) => p.couvre.includes(protocole) && (p.especes.length === 0 || typeof espece !== 'string' || p.especes.includes(espece)))
    .map(([id, p]) => ({ id, nom: p.nom }))
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

function jourPurDe(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
